import { Box, Button, Link, Table, TableBody, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import React from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import GradientButton from "../components/gradients/GradientButton";
import parsePreviewURL from "../utils/parsePreviewURL";
import MobileCartItem from "./mobile/MobileCartItem";
import useMobileMode from "../hooks/useMobileMode";
import CustomizerCartItem from "./CustomizerCartItem";
import ShopifyCreateCheckout from "../utils/ShopifyCreateCheckout";
import editOrderHelper from "../utils/EditOrderHelper";
import { useData } from "../providers/DataProvider";
import { btnStyle } from "./modals/AddToBagDisclaimerModal";

export const StyledLink = styled(Link)(({ theme }) => ({
  textDecoration: "none",
  marginRight: "5px",
  "&:hover": {
    textDecoration: "underline",
  },
}));

const StyledCartWrapper = styled(Box)(({ theme, sx }) => ({
  padding: "25px",
  [theme.breakpoints.down("laptop")]: {
    padding: "25px 0",
    ...sx,
  },
}));

const LineItemsTable = ({ lineItems, handleRemoveItem, modalType, handleEditOrder }) => {
  const isMobile = useMobileMode();

  const CartItem = isMobile ? MobileCartItem : CustomizerCartItem;

  const pointsBalance = lineItems.reduce(
    (sum, item) => sum + item.quantity * item.productPoints - item.quantity * item.points,
    0
  );

  const renderRemoveLink = (item) => {
    if (item.productType === "Patch") {
      return false;
    }
    if (item.productType === "PatchPoints" && modalType === "PREVCART") {
      return false;
    }
    if (item.productType === "PatchPoints" && pointsBalance - item.productPoints < 0) {
      return false;
    }
    if (item.currentCustomization) {
      return false;
    }
    return true;
  };

  return (
    <Table
      sx={{
        "& .MuiTableCell-root": {
          borderTop: "1px solid rgba(224, 224, 224, 1)",
        },
        "& .MuiTableCell-root:not(:nth-of-type(2))": {
          width: "83px",
          whiteSpace: "nowrap",
        },
      }}
    >
      <TableBody>
        {lineItems.map((item, index) => (
          <React.Fragment key={String(index)}>
            <CartItem
              item={item}
              handleRemoveItem={() => {
                handleRemoveItem(item, index);
              }}
              isRemoveBtn={renderRemoveLink(item)}
              handleEditOrder={async () => {
                await handleEditOrder(item, index);
              }}
            />
          </React.Fragment>
        ))}
      </TableBody>
    </Table>
  );
};

const CurrentCart = ({
  lineItems,
  handleRemovePatchPoints,
  handleRemoveShopifyCartItem,
  modalType,
}) => {
  const handleRemoveItem = React.useCallback(
    (item) => {
      if (item.productType === "PatchPoints") {
        handleRemovePatchPoints(item.variantId);
      } else {
        handleRemoveShopifyCartItem(item.variantId);
      }
    },
    [lineItems]
  );

  return (
    <Box sx={{ marginBottom: "50px" }}>
      <Typography variant="h3">Current Design</Typography>
      <LineItemsTable
        lineItems={lineItems}
        handleRemoveItem={handleRemoveItem}
        modalType={modalType}
      />
    </Box>
  );
};

const PreviousCart = ({
  lineItems,
  setPreviousCartLineItems,
  modalType,
  isCartPath,
  setLoading,
  lastIncompleteCheckout,
  isKiosk,
}) => {
  const { token, customer } = useData();
  const [searchParams] = useSearchParams();

  const userHashedId = searchParams.get("c");

  const formattedItems = React.useMemo(() => {
    return lineItems.map((item) => {
      const { patches, previewUrl, points: orderPoints } = parsePreviewURL(item);

      if (previewUrl) {
        const previewUrlValue = isKiosk
          ? previewUrl + `&orderPreviewUid=${userHashedId}`
          : previewUrl;

        return {
          ...item,
          previewUrl: previewUrlValue,
          patches: patches.filter((patch) => {
            for (const item of lineItems) {
              if (item?.productId === patch?.id) {
                return { ...patch, height: item.height, width: item.width };
              }
            }
          }),
          orderPoints,
          customizedItem: true,
        };
      } else {
        return item;
      }
    });
  }, [lineItems]);

  const handleRemoveItem = React.useCallback(
    (item, index) => {
      let updatedItems;
      let transformedPointQuantity;

      if (item.customizedItem) {
        const itemPoints = item?.orderPoints || [];
        const quantityById = {};

        if (itemPoints.length) {
          itemPoints.forEach((item) => {
            const id = item.id;
            if (quantityById[id]) {
              quantityById[id]++;
            } else {
              quantityById[id] = 1;
            }
          });
          transformedPointQuantity = Object.keys(quantityById).map((id) => ({
            id,
            quantity: quantityById[id],
          }));
        }

        setPreviousCartLineItems((prevItem) => {
          updatedItems = prevItem.filter((e, i) => i !== index);
          let i = 0;

          transformedPointQuantity &&
            transformedPointQuantity.forEach(({ id, quantity }, index) => {
              updatedItems = updatedItems.filter((el) => {
                if (el.variantId === id && el.quantity === quantity && index + 1 > i) {
                  i = i + 1;
                  return false;
                } else {
                  return true;
                }
              });
            });

          item.patches.forEach((patch) => {
            const find = updatedItems.find((e) => e.productId === patch.id);
            const findIndex = updatedItems.findIndex((e) => e.productId === patch.id);
            if (find) {
              if (find.quantity > 1) {
                updatedItems = updatedItems.map((e, i) =>
                  i === findIndex ? { ...e, quantity: e.quantity - 1 } : e
                );
              } else {
                updatedItems = updatedItems.filter((e, i) => i !== findIndex);
              }
            }
          });

          // clear PatchPoints if they're the only items left
          const onlyPatchPointsLeft =
            updatedItems.length > 0 &&
            updatedItems.every((item) => item.productType === "PatchPoints");

          return onlyPatchPointsLeft ? [] : updatedItems;
        });
      } else {
        setPreviousCartLineItems((items) => items.filter((e, i) => i !== index));
      }

      return updatedItems;
    },
    [lineItems]
  );

  const handleEditOrder = async (order, index) => {
    setLoading(true);

    const { shopifyPatches, productID, variantID, characterName } = await editOrderHelper(order);

    if (productID && variantID && lastIncompleteCheckout?.customAttributes) {
      ShopifyCreateCheckout(
        token,
        handleRemoveItem(order, index),
        lastIncompleteCheckout.customAttributes
      ).then(() => {
        sessionStorage.setItem(
          `/${productID}/${variantID}`,
          JSON.stringify({ patches: shopifyPatches, points: order?.orderPoints || [] })
        );

        let redirectUrl = `/${productID}/${variantID}?c=${
          userHashedId || btoa(customer?.id.replace("gid://shopify/Customer/", ""))
        }`;

        if (characterName) {
          redirectUrl += `&characterName=${encodeURIComponent(characterName)}`;
        }

        window.location.href = redirectUrl;
      });
    } else {
      setLoading(false);
    }
  };

  if (!formattedItems.length) {
    return null;
  }

  return (
    <Box>
      {!isCartPath && <Typography variant="h3">Previous Designs</Typography>}
      <LineItemsTable
        lineItems={formattedItems}
        handleRemoveItem={handleRemoveItem}
        modalType={modalType}
        handleEditOrder={handleEditOrder}
      />
    </Box>
  );
};

export default function CustomizerCart({
  currentCart,
  previousCart,
  handleRemovePatchPoints,
  handleRemoveShopifyCartItem,
  setPreviousCartLineItems,
  handleContinueShopping,
  handleCheckout,
  setLoading,
  lastIncompleteCheckout,
  handleCreateCheckout,
  isKiosk,
}) {
  const location = useLocation();
  const { customer } = useData();
  const isMobile = useMobileMode();

  const isCartPath = location.pathname === "/cart";
  const CURRENTCART = "CURRENTCART";
  const PREVCART = "PREVCART";
  const isEditOrder = JSON.parse(sessionStorage.getItem(location.pathname))?.length > 0;

  const onCheckout = () => {
    // this checkout func runs from the cart modal from main customizer page
    if (isEditOrder) {
      sessionStorage.removeItem(location.pathname);
    }

    handleCreateCheckout(false);
  };

  return (
    <StyledCartWrapper sx={isCartPath ? { padding: "25px" } : {}}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "25px",
          flexWrap: "wrap",
          gap: "10px",
        }}
      >
        <Typography variant="h2" sx={{ fontSize: "30px" }}>
          Your Order
        </Typography>
        {customer && !isCartPath && (
          <Box sx={{ textAlign: "center", marginTop: "10px" }}>
            <GradientButton addStyle={isMobile ? btnStyle : ""} onClick={onCheckout}>
              Checkout
            </GradientButton>
          </Box>
        )}
        {!currentCart && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
            <Button variant="contained" sx={{ fontSize: "16px" }} onClick={handleContinueShopping}>
              Continue Shopping
            </Button>
            <span>
              <GradientButton
                disabled={!previousCart.length}
                onClick={handleCheckout}
                sx={{ fontSize: "16px" }}
              >
                Checkout
              </GradientButton>
            </span>
          </Box>
        )}
      </Box>
      {currentCart && (
        <CurrentCart
          lineItems={currentCart}
          handleRemovePatchPoints={handleRemovePatchPoints}
          handleRemoveShopifyCartItem={handleRemoveShopifyCartItem}
          modalType={CURRENTCART}
        />
      )}
      {previousCart && (
        <PreviousCart
          lineItems={previousCart}
          setPreviousCartLineItems={setPreviousCartLineItems}
          lastIncompleteCheckout={lastIncompleteCheckout}
          modalType={PREVCART}
          isCartPath={isCartPath}
          setLoading={setLoading}
          isKiosk={isKiosk}
        />
      )}
      {!currentCart?.length && !previousCart?.length && (
        <Typography>You have no items in your cart.</Typography>
      )}
    </StyledCartWrapper>
  );
}
